import {
  updateLetterheadTemplateStatus,
  fetchFirms,
  fetchLetterheadTemplatePreview,
} from "../../../service/settings";

const cleanBase64 = (str) => {
  if (!str || typeof str !== "string") return null;
  let cleaned = str;
  if (cleaned.includes(",")) cleaned = cleaned.split(",")[1];
  return cleaned.trim().replaceAll(/\s/g, "");
};

/**
 * Fetches letterhead preview and returns header image as data URL for thumbnail.
 * Used when list API does not provide thumbnail_url/header_image_url.
 */
export const fetchLetterheadThumbnail = async (templateId) => {
  if (!templateId) return null;
  try {
    const response = await fetchLetterheadTemplatePreview(templateId);
    if (response?.status !== 200) return null;
    const data = response?.data?.data || response?.data;
    const headerUrl = data?.header_image?.url;
    const footerUrl = data?.footer_image?.url;
    if (headerUrl || footerUrl) return headerUrl || footerUrl;
    const headerBase64 = data?.header_image?.base64_content;
    const footerBase64 = data?.footer_image?.base64_content;
    const base64 = cleanBase64(headerBase64) || cleanBase64(footerBase64);
    if (!base64 || base64.length < 20 || base64.startsWith("http")) return null;
    const contentType = data?.header_image?.content_type || data?.footer_image?.content_type || "image/png";
    return `data:${contentType};base64,${base64}`;
  } catch {
    return null;
  }
};

export const handlePreviewUtil = async ({
  template,
  setIsLoading,
  setTemplatePreview,
  setOpenPreview,
  handleToastMessage,
}) => {
  if (!template) return;

  setIsLoading(true);

  try {
    // Try to fetch preview from API first
    try {
      const response = await fetchLetterheadTemplatePreview(template?.id);

      if (response?.status === 200) {
        const data = response?.data?.data || response?.data;

        // New API: header_image and footer_image with base64_content or url
        const headerImage = data?.header_image;
        const footerImage = data?.footer_image;
        const headerBase64 = headerImage?.base64_content;
        const footerBase64 = footerImage?.base64_content;
        const headerUrl = headerImage?.url;
        const footerUrl = footerImage?.url;

        const isValidBase64 = (str) => {
          const cleaned = str ? cleanBase64(str) : null;
          return cleaned && cleaned.length > 20 && !cleaned.startsWith("http");
        };

        if (headerBase64 || footerBase64 || headerUrl || footerUrl) {
          const responseData = {
            file_content: data?.base64_content,
            file_type: "png",
            content_type: "image/png",
            filename: data?.name || template?.name || "letterhead",
            // header_image: headerBase64 && isValidBase64(headerBase64) ? cleanBase64(headerBase64) : null,
            // footer_image: footerBase64 && isValidBase64(footerBase64) ? cleanBase64(footerBase64) : null,
            // header_image_url: headerUrl || null,
            // footer_image_url: footerUrl || null,
          };

          setTemplatePreview(responseData);
          setOpenPreview(true);
          setIsLoading(false);
          return;
        }

        // Legacy: single file_content or base64_content
        let base64Content = data?.file_content || data?.base64_content;

        if (base64Content && typeof base64Content === "string") {
          if (base64Content.includes(",")) {
            base64Content = base64Content.split(",")[1];
          }
          base64Content = base64Content.trim().replaceAll(/\s/g, "");

          const responseData = {
            file_content: base64Content,
            file_type: data?.file_type || template?.file_type || "application/pdf",
            content_type: data?.content_type || data?.file_type || template?.file_type || "application/pdf",
            filename: data?.template_name || data?.filename || template?.file_filename || template?.name || "letterhead",
          };

          setTemplatePreview(responseData);
          setOpenPreview(true);
          setIsLoading(false);
          return;
        }
      }
    } catch (apiError) {
      console.log("API preview not available, trying alternative methods:", apiError);
    }

    // Fallback: If template has file_url, fetch and convert to base64
    if (template.file_url) {
      const response = await fetch(template.file_url);
      const blob = await response.blob();
      
      // Convert blob to base64
      const reader = new FileReader();
      reader.onloadend = () => {
        let base64String = reader.result;
        
        // Remove data URL prefix if present (data:application/pdf;base64,)
        if (base64String.includes(',')) {
          base64String = base64String.split(',')[1];
        }
        // Remove any whitespace
        base64String = base64String.trim().replaceAll(/\s/g, '');
        
        const responseData = {
          file_content: base64String,
          file_type: template.file_type || "application/pdf",
          content_type: template.file_type || "application/pdf",
          filename: template.file_filename || template.name || "letterhead",
        };

        setTemplatePreview(responseData);
        setOpenPreview(true);
        setIsLoading(false);
      };
      reader.onerror = () => {
        console.error("Error converting file to base64");
        handleToastMessage("Failed to convert file for preview.");
        setIsLoading(false);
      };
      reader.readAsDataURL(blob);
      return; // Exit early, will continue in reader callbacks
    } 
    
    // Last resort: If template already has base64 content
    if (template.file_content) {
      let base64Content = template.file_content;
      
      // Clean base64 string - remove data URL prefix if present
      if (typeof base64Content === 'string') {
        if (base64Content.includes(',')) {
          base64Content = base64Content.split(',')[1];
        }
        // Remove any whitespace/newlines
        base64Content = base64Content.trim().replaceAll(/\s/g, '');
      }

      const responseData = {
        file_content: base64Content,
        file_type: template.file_type || "application/pdf",
        content_type: template.file_type || "application/pdf",
        filename: template.file_filename || template.name || "letterhead",
      };

      setTemplatePreview(responseData);
      setOpenPreview(true);
      setIsLoading(false);
    } else {
      handleToastMessage("No file data available for preview.");
      setIsLoading(false);
    }
  } catch (error) {
    console.error("Preview error:", error);
    handleToastMessage("Something went wrong while fetching preview.");
    setIsLoading(false);
  }
};

export const handleDisableTemplateUtil = async ({
  template,
  selectedCategory,
  handleCategoryClick,
  handleToastMessage,
}) => {
  try {
    const newStatus = !template.is_active;
    // Use letterhead-specific update endpoint with template.id
    await updateLetterheadTemplateStatus(template?.id, newStatus);
    await handleCategoryClick(selectedCategory);
  } catch (err) {
    console.error("Failed to update status", err);
    handleToastMessage("Failed to update template status. Try again.");
  }
};

export const handleCreateCategoryUtil = async ({
  categoryName,
  addNewFirm,
  loadInitialTemplates,
  handleToastMessage,
  setAddCategoryModal,
  setIsLoading,
  setCategoryName,
  setError,
  setCategories,
  setSelectedCategory,
  scrollToElement = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "center",
      });
    }
  },
}) => {
  if (!categoryName.trim()) {
    setError("Firm name cannot be empty");
    return;
  }
  if (categoryName.length > 50) {
    setError("Firm name cannot exceed 50 characters");
    return;
  }

  setError("");
  setIsLoading(true);


  try {
    await addNewFirm(categoryName.trim());
    setAddCategoryModal(false);
    
    // Refresh firms list after creating new firm
    const firmsRes = await fetchFirms();
    const firms = firmsRes.data.data || [];
    const mappedCategories = firms.map((firm) => ({
      id: firm.id,
      key: firm.id.toString(),
      label: firm.name,
      count: firm.letterhead_count || 0,
      is_active: firm.is_active,
    }));
    setCategories(mappedCategories);
    
    // Select the newly created firm
    const newFirm = firms.find(f => f.name === categoryName.trim());
    if (newFirm) {
      const newFirmKey = newFirm.id.toString();
      setSelectedCategory(newFirmKey);
      // Load templates for the new firm
      await loadInitialTemplates(newFirmKey);
    }
    
    handleToastMessage("Firm added successfully", true);
    setCategoryName("");
  //  setTimeout(() => scrollToElement(categoryCode), 0);
  } catch (err) {
    console.error("Error adding firm:", err);
    const message = err?.response?.data?.message || "Failed to add firm";
    handleToastMessage(message);
  } finally {
    setIsLoading(false);
  }
};

export const handleUpdateFirmUtil = async ({
  firmName,
  selectedFirm,
  updateFirm,
  loadInitialTemplates,
  handleToastMessage,
  setAddCategoryModal,
  setIsLoading,
  setCategoryName,
  setError,
}) => {
  if (!selectedFirm?.id) {
    handleToastMessage("Select a firm before editing.");
    return;
  }
  if (!firmName.trim()) {
    setError("Firm name cannot be empty");
    return;
  }
  if (firmName.length > 50) {
    setError("Firm name cannot exceed 50 characters");
    return;
  }

  setError("");
  setIsLoading(true);

  try {
    await updateFirm(selectedFirm.id, { name: firmName.trim() });
    setAddCategoryModal(false);
    await loadInitialTemplates(selectedFirm.key);
    handleToastMessage("Firm updated successfully", true);
    setCategoryName("");
  } catch (err) {
    console.error("Error updating firm:", err);
    const message =
      err?.response?.data?.message ||
      err?.response?.data?.error ||
      "Failed to update firm";
    handleToastMessage(message);
  } finally {
    setIsLoading(false);
  }
};

export const handleDeleteFirmUtil = async ({
  selectedFirm,
  deleteFirm,
  loadInitialTemplates,
  handleToastMessage,
  setIsLoading,
  setDeleteFirmModal,
}) => {
  if (!selectedFirm?.id) {
    handleToastMessage("Select a firm before deleting.");
    return;
  }
  if (Number(selectedFirm.count) > 0) {
    handleToastMessage("Delete the letterheads before deleting this firm.");
    return;
  }

  setIsLoading(true);

  try {
    await deleteFirm(selectedFirm.id);
    setDeleteFirmModal(null);
    await loadInitialTemplates();
    handleToastMessage("Firm deleted successfully", true);
  } catch (err) {
    console.error("Error deleting firm:", err);
    const message =
      err?.response?.data?.message ||
      err?.response?.data?.error ||
      "Failed to delete firm";
    handleToastMessage(message);
  } finally {
    setIsLoading(false);
  }
};
